import { zodResolver } from '@hookform/resolvers/zod';
import {
  APPROVED_REFUND_STATUS,
  FAILED_REFUND_STATUS,
  IRefundReqSchema,
  IRefundReqSchemaUI,
  IRefundSchema,
  RefundReasonEnum,
  RefundReqSchema,
} from '@monetize/types/app';
import { formatCurrency } from '@monetize/utils/core';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetPaymentById } from '../../../api/accountsService';
import { handleApiErrorToast } from '../../../api/axios';
import { useCreateRefund } from '../../../api/refundService';
import {
  MAlert,
  MButton,
  MCenterModal,
  MGrid,
  MGridItem,
  MRadio,
  MRadioGroup,
  MSkeleton,
  MStack,
  MText,
} from '../../../components/Monetize';
import { MTooltip } from '../../../components/Monetize/MTooltip';
import { ElectronicRefundDetail } from '../../../components/Refunds/ElectronicRefundDetail';
import { RefundCreateForm } from '../../../components/Refunds/RefundCreateForm';
import { ROUTES } from '../../../constants';
import {
  PaymentMethodTypeEnumDisplay,
  REQUIRES_MANUAL_REFUND,
  shouldUseManualRefund,
} from '../../../constants/paymentMethods';
import { logger } from '../../../services/logger';

interface PaymentRefundModalProps {
  isOpen: boolean;
  paymentId: string;
  onClose: (reload: boolean) => void;
}

export const RefundModal: FC<PaymentRefundModalProps> = ({
  isOpen,
  paymentId,
  onClose,
}: PaymentRefundModalProps) => {
  const navigate = useNavigate();

  const params = useParams();
  const accountId = params?.accountId || '';
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<IRefundReqSchema>();
  const [paymentMode, setPaymentMode] = useState<'MANUAL' | 'ELECTRONIC'>(
    'ELECTRONIC',
  );
  const hasSetPaymentMode = useRef(false);

  const {
    data: freshPayment,
    isLoading: isLoadingPayment,
    isError: isPaymentError,
  } = useGetPaymentById(accountId, paymentId, { refetchOnWindowFocus: false });

  useEffect(() => {
    if (freshPayment && !hasSetPaymentMode.current) {
      hasSetPaymentMode.current = true;
      setPaymentMode(
        REQUIRES_MANUAL_REFUND.has(freshPayment.paymentType)
          ? 'MANUAL'
          : 'ELECTRONIC',
      );
    }
  }, [freshPayment]);

  const { mutateAsync: doCreateRefund, isPending: isLoading } =
    useCreateRefund();

  const RefundPaymentFormSchemaValidator = useMemo(() => {
    return RefundReqSchema.superRefine(({ refundAmount, description }, ctx) => {
      if (
        isNumber(refundAmount) &&
        refundAmount > (freshPayment?.unAppliedAmount ?? 0)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: `Cannot exceed remaining Payment amount of ${formatCurrency(
            freshPayment?.unAppliedAmount,
            { currency: freshPayment?.currency },
          )}`,
          path: ['refundAmount'],
        });
      } else if (isNumber(refundAmount) && refundAmount <= 0) {
        ctx.addIssue({
          code: 'custom',
          message: `Amount must be greater than ${formatCurrency(0, {
            currency: freshPayment?.currency,
          })}`,
          path: ['refundAmount'],
        });
      }

      if (description === '' || description === null) {
        ctx.addIssue({
          code: 'custom',
          message: 'Reason is required',
          path: ['description'],
        });
      }
    });
  }, [freshPayment?.unAppliedAmount, freshPayment?.currency]);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<IRefundReqSchemaUI>({
    resolver: zodResolver(RefundPaymentFormSchemaValidator),
    mode: 'onChange',
    defaultValues: {
      refundAmount: 0,
      description: '',
    },
  });

  useEffect(() => {
    if (freshPayment) {
      reset({
        refundAmount: freshPayment.unAppliedAmount ?? 0,
        description: '',
      });
    }
  }, [freshPayment, reset]);

  const watchedFormValues = watch();

  const shouldResetError = ({
    formData,
    currentValues,
    errorMessage,
  }: {
    formData: IRefundReqSchema | undefined;
    currentValues: IRefundReqSchemaUI;
    errorMessage: string | null;
  }) => {
    if (!formData) {
      return false;
    }

    const currentFormData = {
      refundAmount: Number(currentValues.refundAmount),
      description: currentValues.description,
    };

    return !isEqual(formData, currentFormData) && errorMessage !== null;
  };

  useEffect(() => {
    if (
      shouldResetError({
        formData,
        currentValues: watchedFormValues,
        errorMessage,
      })
    ) {
      setErrorMessage(null);
      setFormData(undefined);
    }
  }, [watchedFormValues, formData, errorMessage]);

  const handleResultSuccess = (result: IRefundSchema) => {
    if (
      result &&
      APPROVED_REFUND_STATUS.has(result.status as RefundReasonEnum)
    ) {
      handleClose(true);
      navigate(
        ROUTES.getRefundView({
          accountId: accountId!,
          refundId: result.id,
        }),
      );
      return true;
    }
    return false;
  };

  const handleResultFailure = (result: IRefundSchema) => {
    if (result && FAILED_REFUND_STATUS.has(result.status as RefundReasonEnum)) {
      setErrorMessage(result.errorMessage);
      return true;
    }
    return false;
  };

  const onSubmit = async (submittedData: IRefundReqSchemaUI) => {
    try {
      const parsedData = RefundReqSchema.parse(submittedData);
      setFormData(parsedData);
      let result: IRefundSchema | null = null;
      // Enforce MANUAL based on the payment type itself, not just the
      // paymentMode state — the state is set by an effect after freshPayment
      // loads, so a fast Confirm click could otherwise route a manual-only
      // payment to the electronic endpoint.
      if (shouldUseManualRefund(paymentMode, freshPayment?.paymentType)) {
        result = await doCreateRefund({
          paymentId,
          payload: parsedData,
          paymentType: 'manual',
        });
      } else {
        result = await doCreateRefund({
          paymentId,
          payload: parsedData,
        });
      }

      if (result) {
        if (!handleResultSuccess(result)) {
          handleResultFailure(result);
        }
      }
    } catch (err) {
      setErrorMessage((err as Error).message);
      setFormData({
        refundAmount: Number(submittedData.refundAmount),
        description: submittedData.description,
      });
      handleApiErrorToast(err);
    }
  };

  const onError = (errs: any) => logger.error(errs);

  function handleClose(reload = false) {
    if (isLoading) {
      return;
    }

    reset();
    onClose && onClose(reload);
  }

  if (isLoadingPayment) {
    return (
      <MCenterModal
        closeOnEsc={false}
        closeOnOverlayClick={false}
        size="md"
        isOpen={isOpen}
        onClose={() => onClose(false)}
        modalTitle="Refund Payment"
      >
        <MSkeleton height="160px" mx={4} />
      </MCenterModal>
    );
  }

  if (isPaymentError || !freshPayment) {
    return (
      <MCenterModal
        closeOnEsc={false}
        closeOnOverlayClick={false}
        size="md"
        isOpen={isOpen}
        onClose={() => onClose(false)}
        modalTitle="Refund Payment"
      >
        <MText color="tRed.base" mx={4}>
          Unable to load payment details. Please close and try again.
        </MText>
      </MCenterModal>
    );
  }

  return (
    <MCenterModal
      closeOnEsc={false}
      closeOnOverlayClick={false}
      size="md"
      isOpen={isOpen}
      onClose={handleClose}
      showCloseButton={!isLoading}
      modalTitle="Refund Payment"
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          <MButton
            isDisabled={isLoading}
            variant="cancel"
            onClick={() => onClose(false)}
            minW="auto"
          >
            Cancel
          </MButton>
          <MButton
            form="refund-form"
            isDisabled={errorMessage !== null || isLoading}
            variant="primary"
            isLoading={isLoading}
            type="submit"
            minW="auto"
          >
            Confirm
          </MButton>
        </MStack>
      )}
    >
      <MGrid alignItems="center" gap={4}>
        <MGridItem colSpan={12}>
          {(freshPayment.appliedAmount ?? 0) > 0 && (
            <MAlert
              type="warning"
              mb={4}
              alertDescriptionProps={{ fontSize: 'sm' }}
            >
              This payment is partially applied to invoices. The maximum refund
              amount is{' '}
              {formatCurrency(freshPayment.unAppliedAmount, {
                currency: freshPayment.currency,
              })}
              .
            </MAlert>
          )}
          <form id="refund-form" onSubmit={handleSubmit(onSubmit, onError)}>
            <MRadioGroup
              value={paymentMode}
              onChange={(value) =>
                setPaymentMode(value as 'MANUAL' | 'ELECTRONIC')
              }
            >
              <MGrid templateColumns="repeat(12, 1fr)" gap={4} mb={3}>
                <MGridItem colSpan={12}>
                  {errorMessage && (
                    <MText color="tRed.base" mb={4}>
                      {PaymentMethodTypeEnumDisplay[freshPayment.paymentType]}{' '}
                      refund failed.
                    </MText>
                  )}
                  <MRadio
                    value="ELECTRONIC"
                    isDisabled={REQUIRES_MANUAL_REFUND.has(
                      freshPayment.paymentType,
                    )}
                  >
                    <MTooltip
                      isDisabled={
                        !REQUIRES_MANUAL_REFUND.has(freshPayment.paymentType)
                      }
                      label="Payment not made from refundable source"
                    >
                      <MText
                        pt="2px"
                        fontWeight={600}
                        color={
                          REQUIRES_MANUAL_REFUND.has(freshPayment.paymentType)
                            ? 'tGray.disabledText'
                            : 'currentcolor'
                        }
                      >
                        Refund Electronically to Original Payment Method
                      </MText>
                    </MTooltip>
                  </MRadio>
                  {paymentMode === 'ELECTRONIC' && (
                    <MGrid gap={4} px={5}>
                      <ElectronicRefundDetail payment={freshPayment} />
                      <RefundCreateForm
                        isLoading={isLoading}
                        control={control}
                        errors={errors}
                        currency={freshPayment?.currency}
                      />
                    </MGrid>
                  )}
                </MGridItem>
                <MGridItem colSpan={12}>
                  <MRadio value="MANUAL">
                    <MText fontWeight={600} pt="2px">
                      Refund Manually
                    </MText>
                  </MRadio>

                  {paymentMode === 'MANUAL' && (
                    <MGrid gap={4} px={5}>
                      <MGridItem colSpan={12}>
                        <MText fontSize={13}>
                          MonetizeNow will record the refund. Send funds
                          separately to your customer.
                        </MText>
                      </MGridItem>
                      <RefundCreateForm
                        isLoading={isLoading}
                        control={control}
                        errors={errors}
                        currency={freshPayment?.currency}
                      />
                    </MGrid>
                  )}
                </MGridItem>
              </MGrid>
            </MRadioGroup>
          </form>
        </MGridItem>
      </MGrid>
    </MCenterModal>
  );
};
